<template>
	<div class="mt-4">
		<h2>{{ $t(headingKey) }}</h2>
		<div
			v-if="applicantProfile && jobOffer"
			class="mb-4 mobile-overflow-table"
		>
			<table class="table mb-0 table-striped">
				<thead v-if="match" class="thead-dark">
					<tr>
						<th></th>
						<th class="w-34">
							{{ $t(`general.label`) }}
						</th>
						<th class="w-33">
							{{ $t('general.job-offer') }}
						</th>
						<th class="w-33">
							{{ $t('general.applicant-profile') }}
						</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<th
							v-if="
								jobOffer.industry && applicantIndustries.length
							"
							:class="industryMatchClass"
							width="50"
						>
							<Icon
								class="w-5"
								:icon="getMatchIcon(industryMatchClass)"
							/>
						</th>
						<th v-else class="match-unknown" width="50">
							<Icon class="w-5" :icon="getMatchIcon('unknown')" />
						</th>
						<th>{{ $t('job-offer.industry') }}</th>
						<td v-if="jobOffer.industry">
							{{ jobOffer.industry[$getLocaleDbColumn('name')] }}
						</td>
						<td v-else>{{ $t('abbreviation.no-answer') }}</td>
						<td v-if="applicantIndustries.length">
							<span
								v-for="(industry, index) in applicantIndustries"
								:key="industry.id"
							>
								<strong v-if="isMatchingIndustry(industry)">
									{{ getIndustryLabel(industry) }}
								</strong>
								<template v-else>
									{{ getIndustryLabel(industry) }}
								</template>
								{{
									index < applicantIndustries.length - 1
										? ', '
										: ''
								}}
							</span>
						</td>
						<td v-else>{{ $t('abbreviation.no-answer') }}</td>
					</tr>
					<tr>
						<th
							v-if="
								jobOffer.occupationalField &&
									applicantProfile.occupationalField
							"
							:class="
								$getMatchClasses(match, 'occupationalField')
							"
							width="50"
						>
							<Icon
								class="w-5"
								:icon="
									getMatchIcon(
										$getMatchClasses(
											match,
											'occupationalField'
										)
									)
								"
							/>
						</th>
						<th v-else class="match-unknown" width="50">
							<Icon class="w-5" :icon="getMatchIcon('unknown')" />
						</th>
						<th>{{ $t('job-offer.occupationalField') }}</th>
						<td v-if="jobOffer.occupationalField">
							{{
								jobOffer.occupationalField[
									$getLocaleDbColumn('name')
								]
							}}
						</td>
						<td v-else>{{ $t('abbreviation.no-answer') }}</td>
						<td v-if="applicantProfile.occupationalField">
							{{
								applicantProfile.occupationalField[
									$getLocaleDbColumn('name')
								]
							}}
						</td>
						<td v-else>{{ $t('abbreviation.no-answer') }}</td>
					</tr>
					<tr>
						<th
							v-if="
								jobOffer.jobPosition &&
									applicantProfile.jobPosition
							"
							:class="$getMatchClasses(match, 'jobPosition')"
							width="50"
						>
							<Icon
								class="w-5"
								:icon="
									getMatchIcon(
										$getMatchClasses(match, 'jobPosition')
									)
								"
							/>
						</th>
						<th v-else class="match-unknown" width="50">
							<Icon class="w-5" :icon="getMatchIcon('unknown')" />
						</th>
						<th>{{ $t('job-offer.jobPosition') }}</th>
						<td v-if="jobOffer.jobPosition">
							{{
								$t('jobPositions.' + jobOffer.jobPosition.name)
							}}
						</td>
						<td v-else>{{ $t('abbreviation.no-answer') }}</td>
						<td v-if="applicantProfile.jobPosition">
							{{
								$t(
									'jobPositions.' +
										applicantProfile.jobPosition.name
								)
							}}
						</td>
						<td v-else>{{ $t('abbreviation.no-answer') }}</td>
					</tr>
				</tbody>
			</table>
		</div>
		<div v-else-if="jobOffer" class="row">
			<div class="p-2 col-12">
				<div
					class="p-3 border rounded bg-light--gradient"
					v-if="jobOffer.industry"
				>
					<label>
						<Icon :icon="['far', 'industry']" />
						{{ $t('job-offer.industry') }}
					</label>
					<span :class="$getMatchClasses(match, 'industry')">
						{{ jobOffer.industry[$getLocaleDbColumn('name')] }}
					</span>
				</div>
			</div>
			<div class="p-2 col-12">
				<div
					class="p-3 border rounded bg-light--gradient"
					v-if="jobOffer.occupationalField"
				>
					<label>
						<Icon :icon="['far', 'compass']" />
						{{ $t('job-offer.occupationalField') }}
					</label>
					<span :class="$getMatchClasses(match, 'occupationalField')">
						{{
							jobOffer.occupationalField[
								$getLocaleDbColumn('name')
							]
						}}
					</span>
				</div>
			</div>
			<div class="p-2 col-12">
				<div
					class="p-3 border rounded bg-light--gradient"
					v-if="jobOffer.companySize"
				>
					<label>
						<Icon :icon="['far', 'building']" />
						{{ $t('job-offer.companySize') }}
					</label>
					<span :class="$getMatchClasses(match, 'companySize')">
						{{ $t('company-sizes.' + jobOffer.companySize.name) }}
					</span>
				</div>
			</div>
			<div class="p-2 col-12">
				<div
					class="p-3 border rounded bg-light--gradient"
					v-if="jobOffer.jobPosition"
				>
					<label>
						<Icon :icon="['far', 'user-cog']" />
						{{ $t('job-offer.jobPosition') }}
					</label>
					<span :class="$getMatchClasses(match, 'jobPosition')">
						{{ $t('jobPositions.' + jobOffer.jobPosition.name) }}
					</span>
				</div>
			</div>
		</div>
		<div v-else-if="applicantProfile" class="row">
			<div class="p-2 col-12">
				<div
					class="p-3 border rounded bg-light--gradient"
					v-if="applicantIndustries.length"
				>
					<label>
						<Icon :icon="['far', 'industry']" />
						{{ $t('job-offer.industry') }}
					</label>
					<span>
						{{ applicantIndustryLabels }}
					</span>
				</div>
			</div>
			<div class="p-2 col-12">
				<div
					class="p-3 border rounded bg-light--gradient"
					v-if="applicantProfile.occupationalField"
				>
					<label>
						<Icon :icon="['far', 'compass']" />
						{{ $t('job-offer.occupationalField') }}
					</label>
					<span :class="$getMatchClasses(match, 'occupationalField')">
						{{
							applicantProfile.occupationalField[
								$getLocaleDbColumn('name')
							]
						}}
					</span>
				</div>
			</div>
			<div class="p-2 col-12">
				<div
					class="p-3 border rounded bg-light--gradient"
					v-if="applicantProfile.companySize"
				>
					<label>
						<Icon :icon="['far', 'building']" />
						{{ $t('job-offer.companySize') }}
					</label>
					<span :class="$getMatchClasses(match, 'companySize')">
						{{
							$t(
								'company-sizes.' +
									applicantProfile.companySize.name
							)
						}}
					</span>
				</div>
			</div>
			<div class="p-2 col-12">
				<div
					class="p-3 border rounded bg-light--gradient"
					v-if="applicantProfile.jobPosition"
				>
					<label>
						<Icon :icon="['far', 'user-cog']" />
						{{ $t('job-offer.jobPosition') }}
					</label>
					<span :class="$getMatchClasses(match, 'jobPosition')">
						{{
							$t(
								'jobPositions.' +
									applicantProfile.jobPosition.name
							)
						}}
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '~/components/ui/common/Icon'
import {
	getIndustryName,
	industryMatchesJob,
	normalizeIndustries,
	orderIndustriesByJobMatch
} from '~/utils/industries'

export default {
	components: {
		Icon
	},
	props: {
		jobOffer: {
			type: Object,
			default: null
		},
		match: {
			type: Object,
			default: null
		},
		applicantProfile: {
			type: Object,
			default: null
		},
		headingKey: {
			type: String,
			default: 'general.details'
		}
	},
	computed: {
		applicantIndustries() {
			return orderIndustriesByJobMatch(
				normalizeIndustries(this.applicantProfile),
				this.jobOffer?.industry
			)
		},
		applicantIndustryLabels() {
			return this.applicantIndustries
				.map(industry => this.getIndustryLabel(industry))
				.filter(Boolean)
				.join(', ')
		},
		industryMatchClass() {
			if (
				!this.jobOffer?.industry ||
				!this.applicantIndustries.length
			) {
				return 'match-unknown'
			}

			if (this.match?.percents?.industry === null) {
				return 'match-unknown'
			}

			return industryMatchesJob(
				this.applicantIndustries,
				this.jobOffer.industry
			)
				? 'matched'
				: 'not-matched'
		}
	},
	methods: {
		getIndustryLabel(industry) {
			return getIndustryName(industry, this.$getLocaleDbColumn('name'))
		},
		isMatchingIndustry(industry) {
			return this.jobOffer?.industry && industry?.id == this.jobOffer.industry.id
		},
		getMatchIcon(matchClass) {
			if (matchClass === 'matched') return ['far', 'check']
			if (matchClass === 'not-matched') return ['far', 'times']
			return ['fas', 'question']
		}
	}
}
</script>
